import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Container } from "@mui/material";
import {
  ConditionalRenderer,
  Header,
  SuccessCreation,
  useLanguageTranslator,
} from "../../../components";
import { PersonEditor } from "../../../features";

import { useNotify } from "../../../components/@common/NotificationsProvider";
import { useMutation, gql } from "@apollo/client";
import type { PersonInputData } from "../../../@lib/types";
import { useApolloQuery } from "../../../hooks";
import { useAuthSession } from "../../../components/AuthSessionProvider";
import { toastHandler } from "../../../utils/helpers";
import { toastTypesKeys } from "../../../utils/constants";
import { ToastContainer } from "react-toastify";

const personCreateMutationDocument = gql`
  mutation PersonCreate($personInput: PersonCreateInput!) {
    personCreate(input: $personInput) {
      person {
        id
      }
    }
  }
`;

const personUpdateMutationDocument = gql`
  mutation PersonUpdate($personUpdateInput: PersonUpdateInput!) {
    personUpdate(input: $personUpdateInput) {
      person {
        id
      }
    }
  }
`;

const personDetailQuery = gql`
  query person($id: ID!) {
    person(id: $id) {
      id
      firstname
      lastname
      country {
        id
      }
      picture {
        source
        originalFilename
      }
      title
      createdAt
      updatedAt
      careers {
        careerActions {
          action {
            description
            id
          }
          actionDate
          id
          theme {
            id
          }
          country {
            id
          }
        }
        careerPositions {
          position {
            description
            id
          }
          positionDate
          id
          theme {
            id
          }
          country {
            id
          }
        }
        createdAt
        endDate
        id
        institution {
          name
          id
        }
        personId
        role {
          name
          id
        }
        startDate
        endDate
        updatedAt
      }
    }
  }
`;

function PersonNew({
  isNotPeopleCreateModified = true,
  onClosePopup = () => {},
}: any) {
  const [imageAttachmentDetails, setImageAttachmentDetails] =
    useState<any>(null);
  const [isOpen, setIsOpen] = useState({
    status: false,
    currId: null,
    module: "",
  });

  const navigate = useNavigate();
  const notify = useNotify();
  const params = useParams();
  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();
  const location = useLocation();
  const isEdit = location?.pathname?.includes("edit");
  const isAdmin = location?.pathname?.includes("admin");

  const [createPerson, { loading }] = useMutation(
    personCreateMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );
  const [updatePerson, { loading: updateLoading }] = useMutation(
    personUpdateMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const { data } = useApolloQuery({
    queryDocument: personDetailQuery,
    variables: { id: params.peopleId, skip: !params.peopleId ? false : true },
  });

  useEffect(() => {
    if (data?.person?.picture) {
      setImageAttachmentDetails(data?.person?.picture);
    }
  }, [data]);

  const handleSave = useCallback(
    async (data: PersonInputData) => {
      try {
        const cleanedData = {
          ...data,
          careers: data.careers.map((career) => {
            const { present, endDate, ...rest }: any = career;
            return {
              ...rest,
              ...(present ? { present: true } : { endDate }),
            };
          }),
        };

        const response = await createPerson({
          variables: {
            personInput: {
              personInput: {
                ...cleanedData,
                ...(imageAttachmentDetails !== null
                  ? {
                      picture: {
                        source:
                          imageAttachmentDetails?.base64URL ||
                          imageAttachmentDetails?.source,
                        originalFilename:
                          imageAttachmentDetails?.name ||
                          imageAttachmentDetails?.originalFilename,
                      },
                    }
                  : {}),
              },
            },
          },
        });

        notify.success("Person created successfuly!");

        if (!isNotPeopleCreateModified) {
          onClosePopup();
        } else {
          // navigate("/submission/people");
          setIsOpen({
            status: true,
            currId: response?.data?.personCreate?.person?.id,
            module: "success",
          });
        }
      } catch (err) {
        notify.error(err as Error);
      }
    },
    [createPerson, navigate, notify, imageAttachmentDetails]
  );

  const handleCancel = useCallback(() => {
    !isNotPeopleCreateModified ? onClosePopup() : navigate(-1);
  }, [navigate]);

  const initialValues: any = useMemo(() => {
    if (!data?.person) return undefined;

    const { firstname, lastname, title, careers, country } = data.person;

    const updatedCareersData = careers?.map(
      ({ institution, role, ...item }: any) => {
        return {
          id: item?.id,
          personId: item?.personId,
          startDate: item?.startDate,
          endDate: item?.endDate,
          institutionId: institution?.id,
          roleId: role?.id,
          careerActions: item?.careerActions?.map(
            ({ action, theme, country, ...item }: any) => {
              return {
                ...item,
                actionId: action?.id,
                themeId: theme?.id,
                countryId: country?.id,
              };
            }
          ),
          careerPositions: item?.careerPositions?.map(
            ({ position, theme, country, ...item }: any) => {
              return {
                ...item,
                positionId: position?.id,
                themeId: theme?.id,
                countryId: country?.id,
              };
            }
          ),
        };
      }
    );

    return {
      firstname: firstname,
      lastname: lastname,
      title: title,
      countryId: country?.id,
      careers: updatedCareersData,
    };
  }, [data]);

  const handleUpdate = useCallback(
    async (data: any) => {
      try {
        const allCareerDestroyed = data.careers.every(
          (item: any) => item?._destroy === true
        );
        if (allCareerDestroyed) {
          toastHandler("Add At least one Career", toastTypesKeys.error);
          return;
        }

        const cleanedData = {
          ...data,
          careers: data.careers.map((career: any) => {
            const { present, endDate, ...rest }: any = career;
            return {
              ...rest,
              ...(present ? { present: true } : { endDate }),
            };
          }),
        };

        const response = await updatePerson({
          variables: {
            personUpdateInput: {
              id: params.peopleId,
              personInput: {
                ...cleanedData,
                ...(imageAttachmentDetails !== null
                  ? {
                      picture: {
                        source:
                          imageAttachmentDetails?.base64URL ||
                          imageAttachmentDetails?.source,
                        originalFilename:
                          imageAttachmentDetails?.name ||
                          imageAttachmentDetails?.originalFilename,
                      },
                    }
                  : {}),
              },
            },
          },
        });

        notify.success("Person Update successfully!");

        if (!isNotPeopleCreateModified) {
          onClosePopup();
        } else {
          navigate(
            isAdmin
              ? `/admin/person_detail?id=${response?.data?.personUpdate?.person?.id}`
              : "/submission/people"
          );
        }
      } catch (err) {
        notify.error(err as Error);
      }
    },
    [updatePerson, navigate, notify, imageAttachmentDetails]
  );

  return (
    <Box mb={!isNotPeopleCreateModified ? 0 : 24}>
      <ToastContainer />
      <SuccessCreation
        isOpen={isOpen}
        newCreationPath={
          isAdmin ? "/admin/people/new" : "/submission/people/new"
        }
        viewPath={isAdmin ? "/admin/person_detail?id=" : "/personDetail?id="}
        module="People"
      />
      <ConditionalRenderer condition={isNotPeopleCreateModified}>
        <Header
          title={
            isEdit ? translator("Update People") : translator("Add new people")
          }
          subtitle=""
        />
      </ConditionalRenderer>
      <Container sx={{ my: 3 }}>
        <PersonEditor
          initialValues={!params.peopleId ? undefined : initialValues}
          disabled={loading}
          onSave={!params.peopleId ? handleSave : handleUpdate}
          onCancel={handleCancel}
          isNotPeopleCreateModified={isNotPeopleCreateModified}
          imageAttachmentDetails={imageAttachmentDetails}
          setImageAttachmentDetails={setImageAttachmentDetails}
          personData={data?.person}
        />
      </Container>
    </Box>
  );
}

export { PersonNew };
